"use client";

import { useRef, useState, type FormEvent } from "react";
import { getMascot } from "@/components/mascot/registry";
import { CONTACT_EMAIL } from "@/lib/site";

const KINDS = [
  ["An app", "App"],
  ["A game", "Game"],
  ["3D or AI, training included", "3D / AI"],
  ["A website", "Website"],
  ["Not sure yet", "Not sure yet"],
] as const;

/**
 * No backend, no lost messages: the form composes the email, then the visitor
 * copies it or opens it in their own mail app. A carries it up to the logo.
 */
export function ContactForm() {
  const [error, setError] = useState("");
  const [letter, setLetter] = useState<{ text: string; href: string } | null>(null);
  const [toast, setToast] = useState("");
  const preRef = useRef<HTMLPreElement>(null);

  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const name = String(data.get("name") ?? "").trim();
    const mail = String(data.get("email") ?? "").trim();
    const msg = String(data.get("message") ?? "").trim();
    const kind = String(data.get("kind") ?? "Not sure yet");
    const focus = (n: string) => (form.elements.namedItem(n) as HTMLElement | null)?.focus();
    if (!name) return (setError("Add your name so Yassine knows who's writing."), focus("name"));
    if (!/^\S+@\S+\.\S+$/.test(mail)) return (setError("That email doesn't look complete. Check it and try again."), focus("email"));
    if (msg.length < 10) return (setError("Tell him a little more about the project, a sentence or two is enough."), focus("message"));
    setError("");
    const text = `Hi Yassine,\n\n${msg}\n\nProject type: ${kind}\n\n${name}\n${mail}`;
    const href = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(`Project inquiry from ${name}`)}&body=${encodeURIComponent(text)}`;
    setLetter({ text, href });
    setToast("");
    getMascot()?.carryLetter();
  };

  const copy = () => {
    if (!letter) return;
    const fail = () => {
      const pre = preRef.current;
      if (pre) {
        const r = document.createRange();
        r.selectNodeContents(pre);
        const s = getSelection();
        s?.removeAllRanges();
        s?.addRange(r);
      }
      setToast("Copying was blocked here. The email is selected: press Ctrl+C or ⌘C.");
    };
    if (!navigator.clipboard?.writeText) return fail();
    navigator.clipboard.writeText(letter.text).then(() => setToast(`Copied. Paste it into an email to ${CONTACT_EMAIL}.`), fail);
  };

  return (
    <form className="card" id="contactCard" data-px="0.86" noValidate onSubmit={submit} aria-label="Project inquiry">
      <div className="field">
        <label htmlFor="fName">Your name</label>
        <input id="fName" name="name" autoComplete="name" required />
      </div>
      <div className="field">
        <label htmlFor="fEmail">Your email</label>
        <input id="fEmail" name="email" type="email" autoComplete="email" required />
      </div>
      <fieldset className="field">
        <legend>What kind of project?</legend>
        <div className="chips">
          {KINDS.map(([value, label], i) => (
            <label key={value}>
              <input type="radio" name="kind" value={value} defaultChecked={i === 0} />
              <span>{label}</span>
            </label>
          ))}
        </div>
      </fieldset>
      <div className="field">
        <label htmlFor="fMsg">Tell him about it</label>
        <textarea id="fMsg" name="message" required placeholder="What are you building, and by when?" />
      </div>
      <p className="err" role="alert">
        {error}
      </p>
      <button className="btn" type="submit">
        Give it to A
      </button>
      {letter && (
        <div className="letter">
          <p className="label">Your email, ready to send</p>
          <pre ref={preRef}>{letter.text}</pre>
          <div className="btns">
            <button className="btn" type="button" onClick={copy}>
              Copy the email
            </button>
            <a className="btn ghost" href={letter.href}>
              Open in your mail app
            </a>
          </div>
          <p className="toast" role="status">
            {toast}
          </p>
        </div>
      )}
    </form>
  );
}
